import React, { useState } from 'react';
import { FileText, CheckSquare, BarChart3, Eye, Download } from 'lucide-react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import ReportOverview from './ReportOverview';
import DataValidation from './DataValidation';
import ChartsGraphs from './ChartsGraphs';
import PreviewReport from './PreviewReport';
import ExportShare from './ExportShare';
import { useTranslation } from 'react-i18next';

const DraftReportWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Define steps configuration for the shared WorkflowTabView
  const steps = [
    {
      id: 1,
      title: t('workflow.draftReport.steps.reportOverview.title'),
      description: t('workflow.draftReport.steps.reportOverview.description'),
      icon: FileText
    },
    {
      id: 2,
      title: t('workflow.draftReport.steps.dataValidation.title'),
      description: t('workflow.draftReport.steps.dataValidation.description'),
      icon: CheckSquare
    },
    {
      id: 3,
      title: t('workflow.draftReport.steps.chartsGraphs.title'),
      description: t('workflow.draftReport.steps.chartsGraphs.description'),
      icon: BarChart3
    },
    {
      id: 4,
      title: t('workflow.draftReport.steps.previewReport.title'),
      description: t('workflow.draftReport.steps.previewReport.description'),
      icon: Eye
    },
    {
      id: 5,
      title: t('workflow.draftReport.steps.exportShare.title'),
      description: t('workflow.draftReport.steps.exportShare.description'),
      icon: Download
    }
  ];
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('draftReportWorkflowData');
      const savedStep = localStorage.getItem('draftReportWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        reportOverview: {},
        dataValidation: {},
        chartsGraphs: {},
        previewReport: {},
        exportShare: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('draftReportWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('draftReportWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
  };

  const handleComplete = () => {
    // Handle completion logic here
    console.log('Draft Report workflow completed');
    // You can add navigation or other completion logic
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'reportOverview';
      case 2: return 'dataValidation';
      case 3: return 'chartsGraphs';
      case 4: return 'previewReport';
      case 5: return 'exportShare';
      default: return 'reportOverview';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <ReportOverview
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 2:
        return (
          <DataValidation
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 3:
        return (
          <ChartsGraphs
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 4:
        return (
          <PreviewReport
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 5:
        return (
          <ExportShare
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      default:
        return <ReportOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkflowTabView
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onComplete={currentStep === 5 ? handleComplete : undefined}
        previewOnly={previewOnly}
      >
        {renderCurrentStep()}
      </WorkflowTabView>
    </div>
  );
};

export default DraftReportWorkflow;
